import React, { useState, useRef, useEffect, useReducer } from 'react';
import { Box, Modal, TextField } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import PostModal from './PostModal';
import StatusModal from './StatusModal';
import TagPeopleModal from './TagPeopleModal';
import ColorPickerModal from './ColorPickerModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { DetailsMenu, Icon, IconGroup } from './details.data';
import LocationModal from './LocationModal';
import { MoreDetails } from './MoreDetails';
import { AuditionRoleGrid } from './AuditionSpecificModals/AuditionRoleGrid';
import { Form } from './Form';
import { useSelector } from 'react-redux';
import { createSystemFormSelector, systemFormSelector } from '../../selectors/selectors';
import { initialState, IPostFormInitialState, reducer } from './reducer/reducer';
import { TSearchAddCategory } from '../../types/global/forms.types';
import { useDispatch } from 'react-redux';
import { createThought } from '../../redux/thougth/action';
import { ICreateThougth } from '../../redux/thougth/types';
import { createAudition } from '../../redux/audition/actions';
import { ICreateAuditionPayload } from '../../redux/audition/types';
import { createEvent } from '../../redux/events/action';
import { ICreateEvent } from '../../redux/events/types';
import { createContestRequest } from '../../redux/requests/action';
import { feedData } from '../../data/feed/feed.data';

interface SlidingContainerProps {
    open: boolean;
    onClose: () => void;
    onFormUpdate?: (key: string, value: string) => void;
    initialCategory?: keyof DetailsMenu;
}

type TView = 'post' | 'audience' | 'photo' | 'tag' | 'color' | 'background' | 'location' | 'more' | 'role' | 'form';

const SlidingContainer: React.FC<SlidingContainerProps> = ({ open, onClose, onFormUpdate, initialCategory }) => {
    const [activeView, setActiveView] = useState<TView>('post');
    const [selectedForm, setSelectedForm] = useState<string | undefined>('');
    const [state, dispatch] = useReducer(reducer, initialState);
    const [, setHistoryStack] = useState<Array<{ view: TView; form?: string }>>([]);
    const [isEdit, setIsEdit] = useState(false);
    const [initialEditValue, setInitialEditValue] = useState<any>();
    const [editIndex, setEditIndex] = useState<any>();
    const actionDispatch = useDispatch();

    // Example: Filter system forms by a specific type
    const systemFormsByTypeSelector = createSystemFormSelector(form => form.identifier === selectedForm);
    const systemSpecificForm = useSelector(systemFormSelector(form => form.form_group === state.category));
    // Example: Use in a component
    const specificSystemForms = useSelector(systemFormsByTypeSelector);
    const [background, setBackground] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const nextViewRef = useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);
    const [more, setMore] = useState<{ category?: string; iconGroup?: IconGroup }>({
        category: '',
    });
    const { themeStyle } = useThemeContext();

    /** Handle All Change activities */
    const handleUpdateForm = (key: keyof IPostFormInitialState, payload: any) => {
        dispatch({
            type: 'FIELD',
            field: key,
            value: payload,
        });
    };

    const handleStatus = (state: string, date?: string, id?: string) => {
        handleUpdateForm('status', state);
        handleUpdateForm('publishStatus', state);
        if (date) {
            handleUpdateForm('scheduled_published_date', date);
        }
    };

    const handleColorSelection = (color: string) => {
        setBackground(color);
        setActiveView('background');
    };

    // Adjust the container's height immediately before changing the active view
    const updateHeight = () => {
        if (nextViewRef.current) {
            requestAnimationFrame(() => {
                setHeight(nextViewRef.current!.scrollHeight + 50);
            });
        }
    };

    useEffect(() => {
        // Update the initial height for the first render
        if (containerRef.current) {
            setHeight(containerRef.current.scrollHeight + 50);
        }
    }, [activeView]);

    const variants = {
        initial: { opacity: 0, x: 50 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -50 },
    };

    const handleMoreClick = (category: keyof DetailsMenu, iconGroup: IconGroup) => {
        setMore({
            category,
            iconGroup,
        });
        setActiveView('more');
    };

    const handleIconClick = (catgeory: keyof DetailsMenu, selectedIcon: Icon) => {
        const { id } = selectedIcon;
        switch (id) {
            case 'add_location':
                handleHistory('location');
                break;
            case 'tag_people':
                handleHistory('tag');
                break;
            case 'add_role':
                handleHistory('role');
                break;
            default:
                handleHistory('form', id);

                break;
        }
    };

    const handleAuditionFormNavigation = () => {
        /** Change these */
        setHistoryStack(prevStack => {
            if (prevStack.length > 0) {
                const lastEntry = prevStack[prevStack.length - 1];
                setActiveView(lastEntry.view);
                setSelectedForm(lastEntry.form);
                return prevStack.slice(0, -1); // Remove the last entry
            }
            return prevStack;
        });
    };

    const handleHistory = (
        newView: TView,
        formId?: string,
        storekey?: string,
        currentFormState?: any,
        index?: number,
    ) => {
        setHistoryStack(prevStack => [...prevStack, { view: activeView, form: selectedForm }]);
        setActiveView(newView);
        setSelectedForm(formId);
        /** Handle autosve hat we have already */
        if (currentFormState) {
            let payload = {
                ...state?.[state.category],
            };

            if (specificSystemForms?.parent_key && specificSystemForms.linked_form_field) {
                const parentKeyToUser = specificSystemForms?.parent_key;
                const subKey = specificSystemForms.linked_form_field.store_key;
                Object.assign(payload, {
                    [parentKeyToUser]: {
                        [subKey]: specificSystemForms.multiple
                            ? [...state?.[state.category]?.[parentKeyToUser]?.[subKey], currentFormState]
                            : currentFormState,
                    },
                });
            }
            if (specificSystemForms?.parent_key && !specificSystemForms.linked_form_field) {
                const parentKeyToUser = specificSystemForms?.parent_key;
                // Ensure the value is an array or default to an empty array
                const existingData = Array.isArray(state?.[state.category]?.[parentKeyToUser])
                    ? state?.[state.category]?.[parentKeyToUser]
                    : [];
                Object.assign(payload, {
                    [parentKeyToUser]: specificSystemForms.multiple
                        ? [...existingData, currentFormState]
                        : currentFormState,
                });
            }
            if (!specificSystemForms?.parent_key) {
                Object.assign(payload, currentFormState);
            }
            if (index) setEditIndex(index);

            handleUpdateForm(state.category as keyof IPostFormInitialState, payload);
        }
    };

    const handleFormSubmit = (formData: any) => {
        if (specificSystemForms?.parent_key) {
            let initial = [];
            let payload;

            // Fetch the initial value for the parent key
            initial = state?.[state.category]?.[specificSystemForms?.parent_key] || []; // This will get the top parent e.g fee_structure

            // Handle linked_form_field logic
            if (specificSystemForms.linked_form_field) {
                const linkedKey = specificSystemForms?.linked_form_field.store_key; // Th ekey underwhich the value should be stored

                if (linkedKey) {
                    if (specificSystemForms.multiple) {
                        // Ensure the value being spread is an array or default to an empty array
                        const existingData = Array.isArray(
                            state?.[state.category]?.[specificSystemForms?.parent_key]?.[linkedKey], //e.g all discounts sub fee_structure
                        )
                            ? state?.[state.category]?.[specificSystemForms?.parent_key]?.[linkedKey]
                            : [];

                        payload = [...existingData, formData];
                        const currentObjectInFocus = state?.[state.category]?.[specificSystemForms?.parent_key].find(
                            (_: any, i: number) => i === editIndex,
                        );

                        // Create a new copy of the object
                        const updatedObject = {
                            ...currentObjectInFocus, // Spread the existing properties
                            [linkedKey]: payload, // Add or update the linkedKey field
                        };
                        const updatedArray = state?.[state.category]?.[specificSystemForms?.parent_key]?.map(
                            (item: any, index: number) => (index === editIndex ? updatedObject : item),
                        );
                        // Update the parent_key with the new storeKey field
                        handleUpdateForm(state.category as keyof IPostFormInitialState, {
                            ...state?.[state.category],
                            [specificSystemForms?.parent_key]: updatedArray,
                        });
                    } else {
                        // If multiple is false, replace or append the object
                        payload = formData;
                        // Update the parent_key with the new storeKey field
                        handleUpdateForm(state.category as keyof IPostFormInitialState, {
                            ...state?.[state.category],
                            [specificSystemForms?.parent_key]: {
                                ...state?.[state.category]?.[specificSystemForms?.parent_key],
                                [linkedKey]: payload,
                            },
                        });
                    }
                }
            } else {
                // Handle non-linked fields as usual
                if (isEdit) {
                    // Replace the item at the editIndex
                    payload = initial.map((item: any, index: number) => (index === editIndex ? formData : item));
                } else {
                    // Add a new item or keep it as a single object if multiple is not allowed
                    payload = !specificSystemForms?.multiple ? { ...formData } : [...initial, formData];
                }

                const pl = {
                    ...state?.[state.category],
                    [specificSystemForms?.parent_key]: payload,
                    forceUpdate: true,
                };

                handleUpdateForm(state.category as keyof IPostFormInitialState, pl);
            }
        } else {
            // Handle cases where parent_key is not defined
            handleUpdateForm(state.category as keyof IPostFormInitialState, {
                ...state?.[state.category],
                ...formData,
            });
        }

        /** Reset Edit Mode */
        setIsEdit(false);
        setInitialEditValue(null);
    };

    const handleOnAddClick = (category?: TSearchAddCategory, formId?: string, currentState?: any) => {
        if (formId) {
            switch (category) {
                case 'castingDirector':
                    handleHistory('form', formId, undefined, currentState);
                    setSelectedForm(formId);
                    break;
            }
        }
    };

    const handleOnSubmit = (payload: ICreateThougth | ICreateAuditionPayload | ICreateEvent) => {
        switch (state.category) {
            case 'thought':
                actionDispatch(createThought(payload as ICreateThougth));
                break;
            case 'audition':
                actionDispatch(createAudition(payload as ICreateAuditionPayload));
                break;
            case 'event':
                actionDispatch(createEvent(payload as ICreateEvent));
                break;
            case 'campaign':
                actionDispatch(
                    createContestRequest({
                        request_type:
                            state && state?.campaign && state?.campaign.campaign_type.value === 'onsite'
                                ? 'campaign_request'
                                : 'initiate_campaign',
                        metadata: {
                            ...payload,
                        },
                    }),
                );
                break;
            default:
                break;
        }
        dispatch({
            type: 'RESET',
        });
        onClose();
    };

    /** This method is primarily used in dev to populate data
     * to avoid typing out long information
     */
    const handleToggleDevData = (checked: boolean) => {
        const selectdCategoryData: any = feedData[state.category];
        if (checked) {
            if (selectdCategoryData) {
                handleUpdateForm('title', selectdCategoryData.title);
                handleUpdateForm('selectedColor', selectdCategoryData.background_color);
                handleUpdateForm(state.category as keyof IPostFormInitialState, selectdCategoryData);
            }
        } else {
            dispatch({
                type: 'RESET',
            });
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    padding: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}>
                <motion.div
                    animate={{ height: height || 'auto' }}
                    transition={{ duration: 0.3 }}
                    style={{
                        overflow: 'scroll',
                        maxWidth: '600px',
                        width: '100%',
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: '16px',
                        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.2)',
                        color: 'white',
                        maxHeight: '700px',
                    }}>
                    <div ref={containerRef} style={{ padding: '20px' }}>
                        <AnimatePresence mode="wait">
                            {activeView === 'post' && (
                                <motion.div
                                    key="post"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight} // Trigger height update before animation
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <PostModal
                                        onClose={onClose}
                                        state={state}
                                        onChangeTitle={text => handleUpdateForm('title', text)}
                                        initialCategory={initialCategory}
                                        onUpdateForm={handleUpdateForm}
                                        themeStyle={themeStyle}
                                        onAudienceClick={() => handleHistory('audience')}
                                        selectedAudience={state['status']}
                                        onCancelBackgroundColor={updateHeight}
                                        onSetBackgroundColor={updateHeight}
                                        onSelectPictureUpload={updateHeight}
                                        onIconClick={handleIconClick}
                                        onMoreClick={handleMoreClick}
                                        onSubmit={handleOnSubmit}
                                        systemForms={systemSpecificForm}
                                        onManualUpdateHeight={updateHeight}
                                        onToggleDevData={handleToggleDevData}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'audience' && (
                                <motion.div
                                    key="audience"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <StatusModal
                                        onClose={onClose}
                                        themeStyle={themeStyle}
                                        onBack={() => handleHistory('post')}
                                        currentStatus={state['status']}
                                        onSelectStatus={handleStatus}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'role' && (
                                <motion.div
                                    key="audience"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <AuditionRoleGrid
                                        onBack={() => handleHistory('post')}
                                        roles={state[state.category]?.role || []}
                                        onClose={onClose}
                                        onAdd={() => {
                                            handleHistory('form', 'add_role');
                                        }}
                                        onDuplicate={role => {
                                            handleUpdateForm(state.category as keyof IPostFormInitialState, {
                                                ...state[state.category],
                                                role: [...state?.[state.category]?.role, role],
                                            });
                                        }}
                                        onDeleteRole={(_, index) => {
                                            const filtered = [...state?.[state.category]?.role].filter(
                                                (_, i) => i !== index,
                                            );
                                            handleUpdateForm(state.category as keyof IPostFormInitialState, {
                                                ...state[state.category],
                                                role: filtered,
                                            });
                                        }}
                                        onEditRole={(role, index) => {
                                            setIsEdit(true);
                                            setEditIndex(index);
                                            setInitialEditValue(role);
                                            handleHistory('form', 'add_role');
                                        }}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'form' && (
                                <motion.div
                                    key="audience"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <Form
                                        onBack={handleAuditionFormNavigation}
                                        onClose={handleAuditionFormNavigation}
                                        form={specificSystemForms}
                                        header={specificSystemForms?.title}
                                        leadingText={specificSystemForms?.form_note}
                                        isEdit={isEdit}
                                        hideGlobalButton
                                        forceUpdate={state[state.category]?.['forceUpdate']}
                                        initialEditValue={initialEditValue}
                                        cancelForceUpdate={() => {
                                            handleUpdateForm(state.category, {
                                                ...state[state.category],
                                                forceUpdate: false,
                                            });
                                        }}
                                        onAddClicked={(category, formId, formState) => {
                                            handleOnAddClick(category, formId, formState);
                                        }}
                                        initialValue={state[state.category]}
                                        onSubmit={handleFormSubmit}
                                        onCreateNewClicked={(formId, storeKey, currentFormState, index) => {
                                            handleHistory('form', formId, storeKey, currentFormState, index);
                                        }}
                                        onDuplicate={(actionValue, form) => {
                                            const getStoreKey = specificSystemForms?.form.find(
                                                f => f.action_button_props?.form === form,
                                            );

                                            if (getStoreKey && getStoreKey.store_key) {
                                                const existing = state?.[state.category]?.[getStoreKey.store_key];
                                                const updatedArray = Array.isArray(existing)
                                                    ? [...existing, actionValue]
                                                    : [actionValue];

                                                handleUpdateForm(state.category as keyof IPostFormInitialState, {
                                                    ...state[state.category],
                                                    [getStoreKey.store_key]: updatedArray,
                                                });
                                            }
                                        }}
                                        onEdit={(actionValue, index, form) => {
                                            setIsEdit(true);
                                            setEditIndex(index);
                                            setInitialEditValue(actionValue);
                                            handleHistory('form', form);
                                        }}
                                        onDelete={(_, index, form) => {
                                            const getStoreKey = specificSystemForms?.form.find(
                                                f => f.action_button_props?.form === form,
                                            );

                                            if (getStoreKey && getStoreKey.store_key) {
                                                const filtered = [
                                                    ...state?.[state.category]?.[getStoreKey.store_key],
                                                ].filter((_, i) => i !== index);
                                                handleUpdateForm(state.category as keyof IPostFormInitialState, {
                                                    ...state[state.category],
                                                    [getStoreKey.store_key]: filtered,
                                                });
                                            }
                                        }}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'more' && (
                                <motion.div
                                    key="photo"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    {more && more.iconGroup && more.iconGroup.icons && (
                                        <MoreDetails
                                            options={more.iconGroup?.icons.filter(icon => icon.id !== 'add_image')} // Removing the image adder as it is a different modal
                                            header={more.iconGroup?.label}
                                            themeStyle={themeStyle}
                                            onBack={() => handleHistory('post')}
                                            onClick={({ id }) => {
                                                handleHistory('form', id);
                                            }}
                                        />
                                    )}
                                </motion.div>
                            )}
                            {activeView === 'tag' && (
                                <motion.div
                                    key="tag"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <TagPeopleModal
                                        onBack={() => setActiveView('post')}
                                        initialUsers={state[state.category]?.['people'] ?? []}
                                        onChange={users => {
                                            const payload = {
                                                ...state[state.category],
                                                people: users,
                                            };
                                            handleUpdateForm(state['category'], payload);
                                        }}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'location' && (
                                <motion.div
                                    key="tag"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <LocationModal
                                        onBack={() => handleHistory('post')}
                                        onRemoveLocation={location => {
                                            const payload = {
                                                ...state[state.category],
                                                location: undefined,
                                            };
                                            handleUpdateForm(state['category'], payload);
                                        }}
                                        themeStyle={themeStyle}
                                        initialSuggestion={state[state.category]?.['location'] ?? undefined} // Object could be undefined
                                        onChange={locationDetails => {
                                            const payload = {
                                                ...state[state.category],
                                                location: locationDetails,
                                            };
                                            handleUpdateForm(state['category'], payload);
                                        }}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'color' && (
                                <motion.div
                                    key="color"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}>
                                    <ColorPickerModal
                                        onBack={() => handleHistory('post')}
                                        onColorSelect={handleColorSelection}
                                    />
                                </motion.div>
                            )}
                            {activeView === 'background' && (
                                <motion.div
                                    key="background"
                                    ref={nextViewRef}
                                    onAnimationStart={updateHeight}
                                    variants={variants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.3 }}
                                    style={{
                                        background: background || '#333333',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '300px',
                                        borderRadius: '8px',
                                    }}>
                                    <TextField
                                        placeholder="What's on your mind?"
                                        variant="standard"
                                        fullWidth
                                        multiline
                                        InputProps={{
                                            disableUnderline: true,
                                            style: { color: 'white', fontSize: '24px', textAlign: 'center' },
                                        }}
                                        sx={{
                                            background: 'transparent',
                                            padding: '16px',
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </Box>
        </Modal>
    );
};

export default SlidingContainer;
